// UTM attribution persistence.
//
// Stores UTM params + derived promo code in localStorage so that a user who
// clicks a UTM link, closes the browser, and returns later via the default URL
// still gets the promo auto-applied and the booking_completed event is
// attributed to the original campaign.
//
// TTL: the promo code's own expires_at date (written after validation).
// If the promo has no expiry (null), the entry lives until the booking
// completes (clearUtmAttribution is called on BookSuccessPage).

import { derivePromoCodeFromSearchParams } from "./bookingAttribution";

const UTM_KEY = "hh:utm_attribution";
const MAX_UTM_TTL_MS = 30 * 24 * 60 * 60 * 1000;

export interface UtmAttribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  /** Promo code derived from UTM params or explicit ?promo= */
  promo_code?: string;
  /**
   * ISO string from Promotion.expires_at — written after the promo is
   * validated server-side. Null means the promo has no expiry date.
   */
  expires_at: string | null;
  captured_at: number;
}

/**
 * Call on every page load that has UTM params in the URL.
 * Only writes when UTM data is present.
 * Does NOT overwrite a still-valid existing attribution — preserves first-touch.
 */
export function saveUtmAttribution(searchParams: URLSearchParams): void {
  const utm_source = searchParams.get("utm_source") ?? undefined;
  const utm_medium = searchParams.get("utm_medium") ?? undefined;
  const utm_campaign = searchParams.get("utm_campaign") ?? undefined;
  const utm_content = searchParams.get("utm_content") ?? undefined;
  const explicit_promo = searchParams.get("promo") ?? undefined;

  if (!utm_source && !utm_campaign && !explicit_promo) return;

  // Preserve first-touch: don't overwrite if a valid attribution already exists
  if (getUtmAttribution()) return;

  const promo_code =
    explicit_promo || derivePromoCodeFromSearchParams(searchParams) || undefined;

  const attribution: UtmAttribution = {
    utm_source,
    utm_medium,
    utm_campaign,
    utm_content,
    promo_code,
    expires_at: null, // filled in later by writeUtmPromoExpiry after validation
    captured_at: Date.now(),
  };

  try {
    localStorage.setItem(UTM_KEY, JSON.stringify(attribution));
  } catch {
    // localStorage unavailable — fail silently
  }
}

/**
 * Call after the UTM-derived promo code is validated server-side.
 * Writes the promo's expires_at into the stored attribution so the TTL
 * is driven by the promo's actual expiry, not an arbitrary number.
 */
export function writeUtmPromoExpiry(expiresAt: string | null): void {
  try {
    const raw = localStorage.getItem(UTM_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as UtmAttribution;
    parsed.expires_at = expiresAt;
    localStorage.setItem(UTM_KEY, JSON.stringify(parsed));
  } catch {
    // Attribution storage is best-effort and should not block booking flow.
  }
}

/**
 * Returns the stored attribution if it exists and has not expired.
 * Prunes stale entries automatically.
 */
export function getUtmAttribution(): UtmAttribution | null {
  try {
    const raw = localStorage.getItem(UTM_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as UtmAttribution;

    if (
      !Number.isFinite(parsed.captured_at) ||
      Date.now() - parsed.captured_at > MAX_UTM_TTL_MS
    ) {
      localStorage.removeItem(UTM_KEY);
      return null;
    }

    // If an expiry date has been written and it has passed, treat as expired
    if (parsed.expires_at && Date.now() > new Date(parsed.expires_at).getTime()) {
      localStorage.removeItem(UTM_KEY);
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

/**
 * Call after a successful booking to clear the attribution so the user's
 * next independent booking starts fresh.
 */
export function clearUtmAttribution(): void {
  try {
    localStorage.removeItem(UTM_KEY);
  } catch {
    // Attribution cleanup is best-effort.
  }
}

/**
 * Call when the UTM-sourced promo code is invalid/expired so it stops being
 * re-applied on future visits. Preserves the UTM campaign attribution itself.
 */
export function clearUtmPromoCode(): void {
  try {
    const raw = localStorage.getItem(UTM_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as UtmAttribution;
    if (!parsed.promo_code) return;
    delete parsed.promo_code;
    localStorage.setItem(UTM_KEY, JSON.stringify(parsed));
  } catch {
    // Attribution cleanup is best-effort.
  }
}
